import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios'
import type { ApiResponse } from '@/types'
import { cacheManager } from '@/utils/cache'
import { toApiError } from './apiError'

export { ApiError, toApiError } from './apiError'
export type { ApiErrorKind } from './apiError'

// Environment variables helper
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  return import.meta.env[key] || defaultValue
}

// API Configuration - UNLIMITED TIMEOUTS
const API_CONFIG = {
  baseURL: getEnvVar('VITE_API_BASE_URL', 'https://deepwind-backend.vercel.app/api'),
  timeout: parseInt(getEnvVar('VITE_API_TIMEOUT', '600000')), // 10 minutes for large uploads
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
}

// Create axios instance
const apiClient: AxiosInstance = axios.create(API_CONFIG)

/** 401 sonrasi login ekraninda gosterilecek sebep metninin sessionStorage anahtari. */
export const AUTH_REASON_KEY = 'deepwind-auth-reason'

/**
 * Sebep AYRICA modul degiskeninde tutulur. `authService.logout()` icindeki
 * `clearAllCache()` `sessionStorage.clear()` cagirdigi icin, SPA icinde kalan
 * yonlendirmelerde sessionStorage'daki kayit silinip mesaj kaybolabiliyordu.
 * Tam sayfa yenilemede modul degiskeni sifirlanir, orada sessionStorage devreye girer.
 */
let inMemoryAuthReason: string | null = null

/** Oturumun neden dustugunu kaydeder (hem bellek hem sessionStorage). */
export const rememberAuthReason = (reason: string): void => {
  if (!reason) return
  inMemoryAuthReason = reason
  try {
    sessionStorage.setItem(AUTH_REASON_KEY, reason)
  } catch {
    // sessionStorage kapali/dolu olabilir; yonlendirmeyi ASLA engellemesin.
  }
}

/** Login ekrani icin: oturumun neden dustugunu bir kez okur ve temizler. */
export const consumeAuthReason = (): string | null => {
  if (inMemoryAuthReason) {
    const reason = inMemoryAuthReason
    inMemoryAuthReason = null
    try {
      sessionStorage.removeItem(AUTH_REASON_KEY)
    } catch {
      // yoksay
    }
    return reason
  }
  try {
    const reason = sessionStorage.getItem(AUTH_REASON_KEY)
    if (reason) sessionStorage.removeItem(AUTH_REASON_KEY)
    return reason
  } catch {
    return null
  }
}

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('deepwind-auth-token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Only add cache buster for non-GET requests or when cache is disabled
    if (config.method !== 'get' || config.params?.noCache) {
    config.params = {
      ...config.params,
      _t: Date.now(),
      }
      delete config.params?.noCache
    }

    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error: AxiosError) => {
    // 401 = kimlik dogrulama dustu. 403 BURAYA GIRMEZ: o bir yetki reddidir ve
    // kullaniciyi oturumdan atmamalidir (bkz. turbineService'teki eski 403 varsayimi).
    if (error.response?.status === 401) {
      // Don't redirect if we're already on login page or doing login request
      const currentPath = window.location.pathname
      const isLoginRequest = error.config?.url?.includes('/auth/login')
      // Oturum dogrulama isteginde SERT yonlendirme yapmayiz: ProtectedRoute ayni
      // anda React Router ile /login'e gidiyor. Ikisi yarisinca sert navigasyon
      // sayfayi bastan yukluyor ve sebep mesaji kullaniciya ulasamiyordu.
      const isSessionCheck = error.config?.url?.includes('/auth/validate-session')

      if (!isLoginRequest && !isSessionCheck && currentPath !== '/login') {
        // Backend oturumun neden dustugunu ayiriyor ('Token suresi dolmus' /
        // 'Oturumunuz sonlandirildi...'). Sert yonlendirme bu bilgiyi kaybettirdigi
        // icin login ekraninin gosterebilmesi adina tasiyoruz.
        const body = error.response.data as { message?: unknown } | undefined
        if (typeof body?.message === 'string') rememberAuthReason(body.message)

        localStorage.removeItem('deepwind-auth-token')
        localStorage.removeItem('deepwind-user')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// Generic API methods
export const api = {
  get: async <T>(url: string, params?: Record<string, unknown>, options?: { useCache?: boolean, cacheTime?: number }): Promise<ApiResponse<T>> => {
    try {
      const { useCache = true, cacheTime = 5 * 60 * 1000 } = options || {}
      const cacheKey = `api_${url}_${JSON.stringify(params || {})}`
      
      // Try to get from cache first (if caching is enabled)
      if (useCache) {
        const cachedData = cacheManager.get<ApiResponse<T>>(cacheKey)
        if (cachedData) {
          return cachedData
        }
      }
      
      // Make API request
      const response = await apiClient.get<ApiResponse<T>>(url, { params })
      const data = response.data
      
      // Cache the response (if caching is enabled)
      if (useCache) {
        cacheManager.set(cacheKey, data, cacheTime)
      }
      
      return data
    } catch (error) {
      throw toApiError(error)
    }
  },

  post: async <T>(url: string, data?: unknown): Promise<ApiResponse<T>> => {
    try {
      const response = await apiClient.post<ApiResponse<T>>(url, data)
      return response.data
    } catch (error) {
      throw toApiError(error)
    }
  },

  put: async <T>(url: string, data?: unknown): Promise<ApiResponse<T>> => {
    try {
      const response = await apiClient.put<ApiResponse<T>>(url, data)
      return response.data
    } catch (error) {
      throw toApiError(error)
    }
  },

  delete: async <T>(url: string): Promise<ApiResponse<T>> => {
    try {
      const response = await apiClient.delete<ApiResponse<T>>(url)
      return response.data
    } catch (error) {
      throw toApiError(error)
    }
  },

  // Cache management methods
  clearCache: (pattern?: string): void => {
    if (pattern) {
      // Clear specific cache entries matching pattern
      cacheManager.clear()
    } else {
      // Clear all cache
      cacheManager.clear()
    }
  },

  invalidateCache: (keys: string[]): void => {
    keys.forEach(key => cacheManager.delete(key))
  }
}

// Specific API endpoints
export const contactApi = {
  sendMessage: async (data: {
    name: string
    email: string
    phone?: string
    subject: string
    message: string
  }): Promise<ApiResponse<{ id: string }>> => {
    return api.post('/contact', data)
  },
}

export const companyApi = {
  getInfo: async (): Promise<ApiResponse<{
    name: string
    description: string
    email: string
    phone: string
    address: string
  }>> => {
    return api.get('/company/info')
  },

  getServices: async (): Promise<ApiResponse<Array<{
    id: string
    title: string
    description: string
    features: string[]
  }>>> => {
    return api.get('/services')
  },

  getProjects: async (): Promise<ApiResponse<Array<{
    id: string
    title: string
    description: string
    technologies: string[]
    demoUrl?: string
    githubUrl?: string
  }>>> => {
    return api.get('/projects')
  },
}

export default apiClient 